import { useState } from 'react';
import type { AdminUser } from '../types';
import { Icon, Avatar, Meter, fmt } from '../atoms';
import { BarChart, Donut } from '../charts';
import { costSeries, tokenSeries, FLAGS, ORG } from '../data';

const MONTH_LABELS = [0, 7, 14, 21, 29].map(i => ({ i, t: 'd' + (i + 1) }));

function KpiCard({ icon, label, value, children }: { icon: string; label: string; value: string | number; children?: React.ReactNode }) {
  return (
    <div className="card kpi">
      <div className="k-h"><span className="ic"><Icon name={icon} size={14} /></span>{label}</div>
      <div className="k-v">{value}</div>
      {children && <div className="k-sub">{children}</div>}
    </div>
  );
}

export function Overview({ users, onOpen, act }: {
  users: AdminUser[];
  onOpen: (u: AdminUser) => void;
  act: (type: string, arg?: unknown) => void;
}) {
  const [mode, setMode] = useState<'cost' | 'tokens'>('cost');
  const pending = users.filter(u => u.status === 'pending');
  const costMonth = costSeries.reduce((a, b) => a + b, 0);
  const db = users.reduce((a, u) => a + u.db, 0);
  const s3 = users.reduce((a, u) => a + u.s3, 0);
  const series = mode === 'cost' ? costSeries : tokenSeries;
  const topCost = [...users].filter(u => u.cost > 0).sort((a, b) => b.cost - a.cost).slice(0, 5);
  const recentFlags = users.filter(u => u.flags.length > 0).slice(0, 4);

  return (
    <>
      <div className="kpis">
        <KpiCard icon="users" label="Total users" value={ORG.totalUsers}>
          <span style={{ color: 'var(--ok)', fontFamily: 'var(--label-font)', fontWeight: 700 }}>+4</span>
          <span>this week</span>
        </KpiCard>
        <KpiCard icon="inbox" label="Pending approval" value={pending.length}>
          {pending.length
            ? <button className="linkbtn" onClick={() => act('goto', 'approvals')}>Review queue →</button>
            : <span>all clear</span>}
        </KpiCard>
        <KpiCard icon="coin" label="AI spend · 30d" value={fmt.cost(costMonth)}>
          <span style={{ color: 'var(--bad)', fontFamily: 'var(--label-font)', fontWeight: 700 }}>+12%</span>
          <span>vs prev 30d</span>
        </KpiCard>
        <KpiCard icon="hdd" label="Storage used" value={fmt.mb(db + s3)}>
          <span>{fmt.mb(db)} db · {fmt.mb(s3)} S3</span>
        </KpiCard>
      </div>

      <div className="grid2">
        <div className="card">
          <div className="panel-h">
            <h3>AI usage · last 30 days</h3>
            <div className="grow" />
            <div className="seg">
              <button className={mode === 'cost' ? 'on' : ''} onClick={() => setMode('cost')}>£ cost</button>
              <button className={mode === 'tokens' ? 'on' : ''} onClick={() => setMode('tokens')}>Tokens</button>
            </div>
          </div>
          <div className="chartwrap">
            <BarChart data={series} xlabels={MONTH_LABELS}
              fmtY={mode === 'cost' ? v => '£' + Math.round(v) : v => fmt.tokens(v * 1000)} />
          </div>
        </div>
        <div className="card">
          <div className="panel-h"><h3>Storage split</h3></div>
          <div className="panel-b">
            <Donut centerV={fmt.mb(db + s3)} centerL="total" slices={[
              { label: 'DynamoDB', value: db, color: 'var(--info)', display: fmt.mb(db), sub: 'structured items & notes' },
              { label: 'S3', value: s3, color: 'var(--accent)', display: fmt.mb(s3), sub: 'attachments & captures' },
            ]} />
          </div>
        </div>
      </div>

      <div className="grid2 even">
        <div className="card">
          <div className="panel-h">
            <h3>Top users by AI cost</h3>
            <div className="grow" />
            <button className="linkbtn" onClick={() => act('goto', 'ai')}>AI usage →</button>
          </div>
          <div className="panel-b">
            <div className="list">
              {topCost.map(u => (
                <div className="lrow" key={u.id} style={{ cursor: 'pointer' }} onClick={() => onOpen(u)}>
                  <Avatar size={32} hue={u.hue} initials={u.initials} />
                  <div className="who">
                    <div className="n">{u.name}</div>
                    <div className="m">{fmt.tokens(u.tokens)} tokens</div>
                  </div>
                  <div style={{ width: 90 }}><Meter pct={fmt.pct(u.cost, u.capCost)} /></div>
                  <div className="val">{fmt.cost(u.cost)}<small>of {fmt.cost(u.capCost)}</small></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="panel-h">
            <h3>Needs attention</h3>
            <div className="grow" />
            <button className="linkbtn" onClick={() => act('goto', 'misuse')}>All flags →</button>
          </div>
          <div className="panel-b">
            {recentFlags.length ? (
              <div className="list">
                {recentFlags.map(u => {
                  const f = u.flags[0], m = FLAGS[f];
                  return (
                    <div className="lrow" key={u.id} style={{ cursor: 'pointer' }} onClick={() => onOpen(u)}>
                      <span className="flagdots"><span className={'fd ' + m.sev} /></span>
                      <div className="who">
                        <div className="n">{u.name}</div>
                        <div className="m">{m.label}</div>
                      </div>
                      <button className="linkbtn" onClick={e => { e.stopPropagation(); act('investigate', u); }}>Investigate</button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ color: 'var(--soft)', padding: '10px 4px' }}>Nothing flagged right now.</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
